import React, { useState, useContext, useEffect } from "react";
import api from "../../services/api";
import { Box, Tooltip } from "@material-ui/core";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';

const packageVersion = require("../../../package.json").version;

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginRight: "15px",
  },
  chip: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 12px",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 600,
    transition: "all 0.3s ease",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    fontFamily: "'Inter', sans-serif",
    border: theme.palette.type === 'dark' 
      ? "1px solid rgba(255, 255, 255, 0.1)" 
      : "1px solid rgba(255, 255, 255, 0.3)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.type === 'dark'
        ? "0 4px 12px rgba(0, 0, 0, 0.25)"
        : "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
  licenseChip: {
    backgroundColor: theme.palette.type === 'dark' 
      ? "rgba(255, 152, 0, 0.15)" 
      : "rgba(255, 255, 255, 0.15)",
    color: theme.palette.type === 'dark' 
      ? "#ff9800" 
      : "#ffffff",
    borderColor: theme.palette.type === 'dark' 
      ? "rgba(255, 152, 0, 0.3)" 
      : "rgba(255, 255, 255, 0.4)",
    animation: "$pulseWarning 2s infinite ease-in-out",
  },
  versionChip: {
    backgroundColor: theme.palette.type === 'dark' 
      ? "rgba(168, 85, 247, 0.15)" // Purple in dark mode
      : "rgba(255, 255, 255, 0.2)", // Glossy white in light mode (assuming dark AppBar)
    color: theme.palette.type === 'dark' 
      ? "#a855f7" 
      : "#ffffff",
    borderColor: theme.palette.type === 'dark' 
      ? "rgba(168, 85, 247, 0.3)" 
      : "rgba(255, 255, 255, 0.5)",
    animation: "$pulsePrimary 3s infinite ease-in-out",
  },
  icon: {
    fontSize: "16px",
    color: "inherit",
  },
  "@keyframes pulseWarning": {
    "0%": { boxShadow: "0 0 0 0 rgba(255, 152, 0, 0.4)" },
    "70%": { boxShadow: "0 0 0 10px rgba(255, 152, 0, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(255, 152, 0, 0)" },
  },
  "@keyframes pulsePrimary": {
    "0%": { 
      boxShadow: theme.palette.type === 'dark' 
        ? "0 0 0 0 rgba(168, 85, 247, 0.4)" 
        : "0 0 0 0 rgba(255, 255, 255, 0.4)" 
    },
    "70%": { 
      boxShadow: theme.palette.type === 'dark' 
        ? "0 0 0 10px rgba(168, 85, 247, 0)" 
        : "0 0 0 10px rgba(255, 255, 255, 0)" 
    },
    "100%": { 
      boxShadow: "0 0 0 0 rgba(0, 0, 0, 0)" 
    },
  },
}));

const VersionControl = () => {
  const classes = useStyles();
  const { returnDays } = useDate();
  const { user, socket } = useContext(AuthContext);
  const [storedVersion] = useState(window.localStorage.getItem("version") || "0.0.0");
  const [backendVersion, setBackendVersion] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchVersion = async () => {
    try {
      const { data } = await api.get("/version");
      setBackendVersion(data.version);
    } catch (err) {
      console.log("[VERSION] Error fetching version:", err);
    }
  };

  useEffect(() => {
    fetchVersion();

    const interval = setInterval(fetchVersion, 60000);

    if (socket) {
      socket.on("version:update", (data) => {
        setBackendVersion(data.version);
      });
    }

    return () => {
      clearInterval(interval);
      if (socket) {
        socket.off("version:update");
      }
    };
  }, [socket]);

  const daysToDueDate = returnDays(user?.company?.dueDate);

  const handleUpdateVersion = async () => {
    try {
      setLoading(true);
      window.localStorage.setItem("version", backendVersion || packageVersion);

      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }

      setTimeout(() => {
        window.location.reload(true);
      }, 800);
    } catch (error) {
      console.error("[VERSION] Update failed:", error);
      setLoading(false);
    }
  };

  return (
    <Box className={classes.container}>
      {/* Notificação de Licença */}
      {daysToDueDate <= 7 && (
        <Tooltip title="Clique para detalhes da licença">
          <Box className={`${classes.chip} ${classes.licenseChip}`} onClick={handleUpdateVersion}>
            <SecurityIcon className={classes.icon} />
            {daysToDueDate === 0 
              ? "Sua licença vence hoje! Clique para atualizar." 
              : `Licença expira em ${daysToDueDate} dias!`
            }
          </Box>
        </Tooltip>
      )}

      {/* Notificação de Nova Versão */}
      {backendVersion && storedVersion !== backendVersion && (
        <Tooltip title={`Versão ${backendVersion} disponível! Clique para atualizar.`}>
          <Box className={`${classes.chip} ${classes.versionChip}`} onClick={handleUpdateVersion}>
             <SystemUpdateIcon className={classes.icon} />
             {loading ? "Atualizando..." : "Nova versão disponível!"}
             {!loading && <RefreshIcon className={classes.icon} style={{ marginLeft: '4px' }} />}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default VersionControl;
